
import React, { useEffect, useRef } from 'react';
import { EstateProfile, EstateAssets, TNRBConfidence, FormerDisposalKind } from '../../types';
import { Users, FileText, HeartHandshake, Info, Home, Heart, TrendingDown, Shield, AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';
import { HelpPopover } from '../HelpPopover';
import { HELP_CONTENT } from '../helpContent';
import { emitProbateScroll } from '../probate/scrollTargets';
import { deriveHasGiftWithReservation } from '../probate/deriveReviewFlags';
import { applyFormerHomeAnswer, readFormerResidenceDisposed, FormerResidenceDisposed, readFormerDisposals, formerDisposalNominationPrompt, reconcileFormerNomination, formerDisposalSameDay, FormerDisposalLike } from '../../server/services/rnrbResidenceFacts';

/** RNRB former-home question and help (legal/reports/rnrb-residence-history-decisions.md §6, exact wording). */
export const FORMER_HOME_QUESTION = (name: string) => `On or after 8 July 2015 and before they died, did ${name} sell or give away a home they had lived in, or a share in one?`;
export const FORMER_HOME_QUALIFIER = 'Count it only if they lived there while they owned it, before it was sold or given away.';
export const FORMER_HOME_HELP = 'Use the date the sale or gift was completed, not the date contracts were exchanged. If they gave a home away but carried on living in it or benefiting from it, choose "Not sure".';
/** Downsizing cap help (D33; PA-IHT-RNRB-DOWNSIZING-001 E11, E25; IHT435 note 17). Not a statement of the whole s.8K class. */
export const DOWNSIZING_CAP_HELP = 'Include everything else in the estate that passes to direct descendants (children, grandchildren and their descendants, including step-children and foster children, and their spouses or civil partners), including land and other property such as a let flat or a second home, but not the home used for the residence nil-rate band. If the estate holds no home they lived in, include everything that passes to them. Use the value after deducting any exemptions, agricultural or business relief, and liabilities.';
export const FORMER_HOME_OPTIONS: Array<{ value: FormerResidenceDisposed; label: string }> = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unsure', label: 'Not sure' },
];
/**
 * Several disposals and the PRs' nomination (D42; s.8H(4A)-(4C), PA-IHT-RNRB-DOWNSIZING-001 E15; IHTM46053 Example 2;
 * IHT435 note 14). One entry is one home on one day; same-day parts of one home are combined.
 */
export const FORMER_DISPOSALS_ADD = 'They sold or gave away more than one home, or parts of one home on different days';
export const FORMER_DISPOSALS_HELP = 'Add one entry for each home and each day. If parts of the same home were sold or given away on the same day (for example, shares given to two children on the same day), put them in one entry with the combined share. Different homes sold or given away on the same day stay as separate entries.';
/** Same-day disposals (D42; s.8H(4B)(a)(ii)): parts of one home on one day are one disposal. Asked per set of same-date entries. */
export const FORMER_DISPOSAL_SAME_DAY_QUESTION = 'These entries have the same date. Are any of them parts of the same home?';
export const FORMER_DISPOSAL_SAME_DAY_OPTIONS: Array<{ value: 'yes' | 'no' | 'unsure'; label: string }> = [
    { value: 'yes', label: 'Yes — parts of the same home' },
    { value: 'no', label: 'No — they are different homes' },
    { value: 'unsure', label: 'Not sure' },
];
export const FORMER_DISPOSAL_SAME_DAY_COMBINE = 'Parts of the same home sold or given away on the same day count together as one. Enter them as one entry: keep one, enter the combined share disposed of that day (for example 100 for two 50% shares), and remove the others. No downsizing addition is included until then.';
export const FORMER_DISPOSAL_SAME_DAY_UNSURE = 'No downsizing addition is included until this is known: if they are parts of the same home, they count together as one.';
export const FORMER_DISPOSAL_UNDATED = 'Enter the date for each entry, so we can tell whether any were sold or given away on the same day.';
export const FORMER_DISPOSAL_LIVED_IN = (name: string) => `Had ${name} lived in this home while they owned it, before this sale or gift?`;
/** D36 special disposals (PA-IHT-RNRB-DOWNSIZING-001 E17; s.8H(4D), (4E); s.8HA; decision record Amendment A5). */
export const FORMER_DISPOSAL_KIND_QUESTION = 'How did they part with it?';
export const FORMER_DISPOSAL_KIND_OPTIONS: Array<{ value: FormerDisposalKind; label: string }> = [
    { value: 'sale_or_gift', label: 'Sold it, or gave it away and stopped benefiting from it' },
    { value: 'gift_benefit_kept', label: 'Gave it away but kept living there or benefiting from it until they died' },
    { value: 'gift_benefit_ended', label: 'Gave it away, kept benefiting for a time, then stopped before they died' },
    { value: 'trust_disposal', label: 'It was held in a trust for them, and the trustees sold it or their right to it ended' },
    { value: 'conditionally_exempt', label: 'Given away as a conditionally exempt transfer (heritage property)' },
    { value: 'unsure', label: 'Not sure' },
];
export const FORMER_DISPOSAL_KIND_HELP: Partial<Record<FormerDisposalKind, string>> = {
    gift_benefit_kept: 'A home given away that they kept living in or benefiting from until they died is not treated as sold or given away for the downsizing addition, so it does not count here. It may still count as part of their estate: record it on the property page.',
    gift_benefit_ended: 'Use the date they stopped benefiting, and the value at that date: that is when it counts as given away.',
    trust_disposal: 'Whether this counts depends on the kind of trust, which this tool does not work out. No downsizing addition is included for it; get professional advice.',
    conditionally_exempt: 'A conditionally exempt transfer cannot count for the downsizing addition, unless the exemption was lost before they died.',
};
const FormerDisposalKindQuestion: React.FC<{ name: string; value: unknown; onChange: (v: FormerDisposalKind) => void }> = ({ name, value, onChange }) => (
    <div data-testid="former-disposal-kind">
        <p className="text-[11px] font-bold text-ink2 mb-1">{FORMER_DISPOSAL_KIND_QUESTION}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1" role="radiogroup">
            {FORMER_DISPOSAL_KIND_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer p-1.5 rounded border border-rule text-xs">
                    <input type="radio" name={name} value={opt.value} checked={value === opt.value} onChange={() => onChange(opt.value)} />
                    {opt.label}
                </label>
            ))}
        </div>
        {typeof value === 'string' && FORMER_DISPOSAL_KIND_HELP[value as FormerDisposalKind] && (
            <p className="text-[10px] text-ink3 mt-1">{FORMER_DISPOSAL_KIND_HELP[value as FormerDisposalKind]}</p>
        )}
    </div>
);
export const FORMER_DISPOSAL_NOMINATION_QUESTION = 'Only one of these can count for the downsizing addition: one home and, if parts of it were sold or given away on different days, one day. The executors or administrators choose which. This is called nominating. Which have they nominated?';
export const FORMER_DISPOSAL_NOMINATION_HELP = 'The choice can change the amount. The nominated disposal date is the one that goes on form IHT435.';
const newFormerDisposalId = () => `fd-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const formerDisposalLabel = (d: FormerDisposalLike, i: number) => {
    const name = typeof d.label === 'string' && d.label.trim() ? d.label.trim() : `Home ${i + 1}`;
    const date = typeof d.disposal_date === 'string' && d.disposal_date ? new Date(d.disposal_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'date not entered';
    return `${name} — ${date}`;
};

interface Props {
    data: EstateProfile;
    onChange: (profile: EstateProfile) => void;
    /**
     * Estate assets snapshot — needed so trust-screening derivation can pick up
     * GWR signals stored on individual property records (PropertyAsset.gift_with_reservation),
     * not just the estate-level trust_screening.has_gift_with_reservation flag.
     */
    assets: EstateAssets;
    goToStep?: (stepId: string) => boolean;
}

// ---------------------------------------------------------------------------
// TNRB confidence derivation
// ---------------------------------------------------------------------------
function deriveTNRBConfidence(sp: NonNullable<NonNullable<EstateProfile['iht_details']>['predeceased_spouse']>): TNRBConfidence {
    if (!sp.exists) return 'none';

    // If manual override with a percentage entered, treat as confirmed
    if (sp.manual_override && sp.unused_nrb_percent != null && sp.unused_nrb_percent > 0) return 'confirmed';

    const leftToDeceased = sp.estate_left_to_deceased;
    const hadTrusts = sp.had_trusts_or_gifts;
    const records = sp.records_available;

    // If first spouse left everything and no trusts/gifts → 100% confirmed
    if (leftToDeceased === 'all' && hadTrusts === false && (records === 'full' || records === 'partial')) {
        return 'confirmed';
    }

    // If first spouse left most/all and records available → confirmed (user should enter %)
    if ((leftToDeceased === 'all' || leftToDeceased === 'most') && hadTrusts === false && records === 'full') {
        return 'confirmed';
    }

    // Known that no NRB was unused
    if (leftToDeceased === 'none') return 'none';

    // Had trusts or partial info → possible but uncertain
    if (hadTrusts === true || records === 'none' || leftToDeceased === 'unknown') {
        return 'possible';
    }

    // Partial estate left → possible
    if (leftToDeceased === 'partial') return 'possible';

    // Default: possible if we have some info
    if (leftToDeceased || records) return 'possible';

    return 'possible';
}

// ---------------------------------------------------------------------------
// Trust screening confidence derivation
//
// `hasGWR` is precomputed by the caller via `deriveHasGiftWithReservation`
// so per-property GWR signals (PropertyAsset.gift_with_reservation === 'yes')
// flow into the same derivation as the estate-level trust_screening flag.
// ---------------------------------------------------------------------------
function deriveTrustConfidence(
    ts: NonNullable<NonNullable<EstateProfile['iht_details']>['trust_screening']>,
    hasGWR: boolean,
): 'standard' | 'caution' | 'specialist_review' {
    const trustDetected = ts.created_or_transferred_trust === true || ts.benefited_from_trust === true;
    const hasLoanTrust = ts.has_loan_trust_or_discounted_gift === true;
    const uncertain = ts.uncertain === true;

    if (hasLoanTrust) return 'specialist_review';
    if (trustDetected && ts.trust_type === 'discretionary') return 'specialist_review';
    if (hasGWR && ts.gwr_market_rent_paid === false) return 'specialist_review'; // GWR likely
    if (trustDetected && ts.trust_type === 'interest_in_possession') return 'caution';
    if (hasGWR) return 'caution';
    if (trustDetected) return 'caution';
    if (uncertain) return 'caution';

    return 'standard';
}

/** Derive all trust/GWR flags from screening answers + derived GWR signal. */
function deriveTrustFlags(
    ts: NonNullable<NonNullable<EstateProfile['iht_details']>['trust_screening']>,
    hasGWR: boolean,
) {
    const trustDetected = ts.created_or_transferred_trust === true || ts.benefited_from_trust === true;
    const hasLoanTrust = ts.has_loan_trust_or_discounted_gift === true;

    const gwrLikely = hasGWR && (ts.gwr_continued_use != null || ts.gwr_market_rent_paid === false);
    const gwrPossible = hasGWR && !gwrLikely;

    const specialistNeeded = hasLoanTrust ||
        (trustDetected && ts.trust_type === 'discretionary') ||
        gwrLikely;

    return {
        trust_involvement_detected: trustDetected,
        trust_involvement_uncertain: ts.uncertain === true,
        gwr_possible: gwrPossible,
        gwr_likely: gwrLikely,
        specialist_review_needed: specialistNeeded,
        // Legacy compat
        has_trusts: trustDetected,
    };
}

export function deriveTRNRBConfidence(sp: NonNullable<NonNullable<EstateProfile['iht_details']>['predeceased_spouse']>): TNRBConfidence | null {
    // Need the first death date to assess.
    if (!sp.date_of_first_death) return null;

    // First death BEFORE 6 April 2017: RNRB did not exist, so none could have
    // been used — the brought-forward allowance is 100% (IHTM46040, IHTA84/S8G(4)).
    // (This is the headline fix: previously this returned null, hiding £175k.)
    if (new Date(sp.date_of_first_death) < new Date('2017-04-06')) return 'confirmed';

    // If estate was fully exempt (everything to surviving spouse), RNRB was preserved → 100% transfer
    if (sp.first_spouse_estate_exempt === true) return 'confirmed';

    // If first spouse had qualifying residence passing to descendants → RNRB was
    // used. Whether ANY transfers depends on how much: a partial use can still
    // leave a slice. If the user captured the residence value, derive it; if not,
    // we can't yet claim a transfer → 'none' (the prior behaviour).
    if (sp.first_spouse_had_qualifying_residence === true && sp.first_spouse_residence_to_descendants === true) {
        const pct = deriveSuggestedRnrbPercent(sp);
        if (pct != null) return pct > 0 ? 'confirmed' : 'none';
        return 'none';
    }

    // If no qualifying residence → full RNRB unused → available for transfer
    if (sp.first_spouse_had_qualifying_residence === false) return 'confirmed';

    // If had qualifying residence but it didn't pass to descendants → RNRB unused → confirmed
    if (sp.first_spouse_had_qualifying_residence === true && sp.first_spouse_residence_to_descendants === false) return 'confirmed';

    // Uncertain / incomplete info
    return 'possible';
}

/**
 * Residential Enhancement (the RNRB "base") in force at a given death date.
 * Phased in from 6 April 2017 and frozen at £175,000 since 2020/21
 * (IHTA84/S8D(5)). This is the denominator when deriving a transferable
 * percentage from a partially-used first estate — ALWAYS the first death's
 * tax year, never the survivor's. Returns null for deaths before the RNRB
 * existed (pre-6 April 2017), where the deemed-£100k rule in
 * deriveSuggestedRnrbPercent applies instead.
 */
export function residentialEnhancement(dateOfDeath: string): number | null {
    const d = new Date(dateOfDeath);
    if (d < new Date('2017-04-06')) return null;
    if (d < new Date('2018-04-06')) return 100_000; // 2017/18
    if (d < new Date('2019-04-06')) return 125_000; // 2018/19
    if (d < new Date('2020-04-06')) return 150_000; // 2019/20
    return 175_000;                                 // 2020/21 onwards (frozen)
}

/**
 * Suggested unused RNRB % to transfer (brought-forward allowance), mirroring the
 * NRB auto-suggest. Returns a percentage to OFFER (click-to-apply) — never a
 * silent default.
 *
 * Three paths:
 *  • Fully-unused (pre-2017 / spouse-exempt / no residence) → 100%, tapered on
 *    the DEEMED £100k for a pre-2017 first death (cliff at £2.2m).
 *  • Partial-use: the first spouse's residence passed to direct descendants, so
 *    SOME RNRB was used. When the user supplies the residence value that passed
 *    to descendants, derive the unused % against the FIRST-death-year
 *    Residential Enhancement (IHTA84/S8G). See the denominator note below.
 *  • Otherwise null — facts are insufficient, so the user enters it manually.
 */
export function deriveSuggestedRnrbPercent(
    sp: NonNullable<NonNullable<EstateProfile['iht_details']>['predeceased_spouse']>,
): number | null {
    if (!sp.exists || !sp.date_of_first_death) return null;

    const pre2017 = new Date(sp.date_of_first_death) < new Date('2017-04-06');
    const fullyExempt = sp.first_spouse_estate_exempt === true;
    const noResidence = sp.first_spouse_had_qualifying_residence === false;
    const v = sp.first_estate_value;

    // ── Fully-unused path ────────────────────────────────────────────
    const fullyUnused = pre2017 || fullyExempt || noResidence;
    if (fullyUnused) {
        // Taper: a first estate over the £2m threshold reduces the brought-forward
        // amount by £1 for every £2 over £2m. CRUCIAL: for a pre-2017 first death the
        // taper bites on the DEEMED residential enhancement of £100,000 (IHTA84/S8G(4)–(5)),
        // not the £175k at the second death. So the cliff is £2.2m (£100k ÷ 2 = £100k
        // of headroom), and the result is expressed as a percentage of that enhancement.
        if (v != null && v > 2_000_000) {
            const reduction = (v - 2_000_000) / 2;
            if (pre2017) {
                const DEEMED = 100_000;
                const unused = Math.max(0, DEEMED - reduction);
                return Math.round((unused / DEEMED) * 100); // 0 at/after the £2.2m cliff
            }
            // Post-2017 fully-unused first estate over £2m: kept manual to stay in
            // scope of the (already-shipped) 100% path; the partial path below is
            // the supported route once a used residence is captured.
            return null;
        }
        return 100;
    }

    // ── Partial-use path ─────────────────────────────────────────────
    // The first spouse HAD a qualifying residence that passed to direct
    // descendants, so SOME RNRB was used. Derive the unused % from the residence
    // value the user captured, against the first-death-year enhancement.
    //
    // CRITICAL: the denominator is the FULL (un-tapered) RE for the first-death
    // year. The £2m taper reduces only the NUMERATOR (the available allowance);
    // dividing by the tapered allowance over-claims the transfer — the very bug
    // this capture exists to prevent (IHTA84/S8G(5)).
    const residenceToDescendants = sp.first_spouse_residence_to_descendants_value;
    if (
        sp.first_spouse_had_qualifying_residence === true &&
        sp.first_spouse_residence_to_descendants === true &&
        residenceToDescendants != null
    ) {
        const RE_first = residentialEnhancement(sp.date_of_first_death);
        if (RE_first == null) return null; // pre-2017 handled above; defensive

        // The first spouse's OWN brought-forward RNRB (a multi-spouse chain) would
        // be added here; >1 predeceased spouse is not modelled yet, so this is 0.
        const defaultAllow = RE_first;
        const adjustedAllow = (v != null && v > 2_000_000)
            ? Math.max(0, defaultAllow - (v - 2_000_000) / 2)
            : defaultAllow;
        const usedRNRB = Math.min(residenceToDescendants, adjustedAllow);
        const unused = Math.max(0, adjustedAllow - usedRNRB);
        // Capped at 100% (forward guard for a multi-spouse total; a single spouse
        // can't exceed its own enhancement, but the cap is made explicit).
        const transferPercent = Math.min(100, (unused / RE_first) * 100);
        // Two-decimal precision: the same cash gives a non-integer % when the
        // denominator differs by year (e.g. £100k ÷ £175k = 57.14%).
        return Math.round(transferPercent * 100) / 100;
    }

    return null;
}

/**
 * Human-readable basis for a partial-use derivation, shown beside the suggested
 * percentage (e.g. "first estate over £2m, £55,000 residence to descendants vs
 * the £175,000 first-year allowance"). Returns null when the partial path does
 * not apply.
 */
export function deriveRnrbPartialReason(
    sp: NonNullable<NonNullable<EstateProfile['iht_details']>['predeceased_spouse']>,
): string | null {
    if (
        sp.first_spouse_had_qualifying_residence !== true ||
        sp.first_spouse_residence_to_descendants !== true ||
        sp.first_spouse_residence_to_descendants_value == null ||
        !sp.date_of_first_death
    ) return null;
    const RE = residentialEnhancement(sp.date_of_first_death);
    if (RE == null) return null;
    const v = sp.first_estate_value;
    const parts: string[] = [];
    if (v != null && v > 2_000_000) parts.push('first estate over £2m');
    parts.push(
        `£${sp.first_spouse_residence_to_descendants_value.toLocaleString()} residence to descendants vs the £${RE.toLocaleString()} first-year allowance`,
    );
    return parts.join(', ');
}

export const BeneficiariesStep: React.FC<Props> = ({ data, onChange, assets, goToStep }) => {
    // Former-home gateway (Slice 5). The explicit answer is the authority; `applies` is no longer written here and is
    // read only by the legacy fallback of an unanswered, unstamped estate.
    const formerHome = readFormerResidenceDisposed(data.iht_details?.downsizing_allowance?.former_residence_disposed);
    const legacyDownsizingRecorded = !formerHome && data.iht_details?.downsizing_allowance?.applies === true;
    const formerHomeName = data.deceased?.firstName?.trim() || 'the deceased';
    const setFormerHome = (answer: FormerResidenceDisposed) => onChange({
        ...data,
        iht_details: { ...(data.iht_details || {}), downsizing_allowance: applyFormerHomeAnswer(data.iht_details?.downsizing_allowance, answer) as any },
    });

    // Several former-home disposals (D42). Without a list the single record's fields are edited, as before. Adding a
    // second disposal moves the single record into a list of two; removing back to one entry returns it to the single
    // record (unless that entry is explicitly not lived in). A nomination that no longer resolves is cleared.
    const downsizing: any = data.iht_details?.downsizing_allowance ?? {};
    const formerDisposals = readFormerDisposals(downsizing);
    const dateOfDeath = data.deceased?.date_of_death;
    const formerNomination = formerDisposalNominationPrompt(downsizing, dateOfDeath);
    const formerSameDay = formerDisposalSameDay(downsizing, dateOfDeath);
    const writeDownsizing = (next: Record<string, any>) => onChange({
        ...data,
        iht_details: { ...(data.iht_details || {}), downsizing_allowance: reconcileFormerNomination(next, dateOfDeath) as any },
    });
    const blankDisposal = () => ({ id: newFormerDisposalId(), label: null, disposal_date: null, former_property_value: null, former_property_share_percent: null, lived_in: null });
    const addFormerDisposal = () => {
        if (formerDisposals) { writeDownsizing({ ...downsizing, former_residence_disposals: [...formerDisposals, blankDisposal()] }); return; }
        const first = { ...blankDisposal(), disposal_date: downsizing.disposal_date ?? null, former_property_value: downsizing.former_property_value ?? null, former_property_share_percent: downsizing.former_property_share_percent ?? null, disposal_kind: downsizing.disposal_kind ?? null };
        writeDownsizing({ ...downsizing, former_residence_disposals: [first, blankDisposal()], nominated_disposal_id: null, disposal_date: null, former_property_value: null, former_property_share_percent: null, disposal_kind: null });
    };
    const updateFormerDisposal = (id: unknown, field: string, value: unknown) => writeDownsizing({
        ...downsizing, former_residence_disposals: (formerDisposals ?? []).map((d) => (d.id === id ? { ...d, [field]: value } : d)),
    });
    const removeFormerDisposal = (index: number) => {
        const rest = (formerDisposals ?? []).filter((_, i) => i !== index);
        if (rest.length === 1 && rest[0].lived_in !== 'no') {
            const d = rest[0];
            writeDownsizing({ ...downsizing, former_residence_disposals: null, nominated_disposal_id: null, disposal_date: d.disposal_date ?? null, former_property_value: d.former_property_value ?? null, former_property_share_percent: d.former_property_share_percent ?? null, disposal_kind: d.disposal_kind ?? null });
            return;
        }
        writeDownsizing({ ...downsizing, former_residence_disposals: rest.length ? rest : null });
    };
    // Same-day answer (D42): recorded for exactly the entries sharing the date; replaces any earlier answer for that set.
    const answerSameDay = (ids: string[], same_home: 'yes' | 'no' | 'unsure') => {
        const others = (Array.isArray(downsizing.same_day_checks) ? downsizing.same_day_checks : [])
            .filter((c: any) => !(Array.isArray(c?.disposal_ids) && c.disposal_ids.length === ids.length && [...c.disposal_ids].sort().every((id: string, i: number) => id === ids[i])));
        writeDownsizing({ ...downsizing, same_day_checks: [...others, { disposal_ids: ids, same_home }] });
    };

    // Helper to update deeply nested state
    const update = (path: string[], value: any) => {
        const newData = { ...data };
        let current: any = newData;
        for (let i = 0; i < path.length - 1; i++) {
            if (!current[path[i]]) current[path[i]] = {};
            current = current[path[i]];
        }
        current[path[path.length - 1]] = value;
        onChange(newData);
    };

    // Multi-field update helper
    const updateMany = (updates: [string[], any][]) => {
        const newData = { ...data };
        for (const [path, value] of updates) {
            let current: any = newData;
            for (let i = 0; i < path.length - 1; i++) {
                if (!current[path[i]]) current[path[i]] = {};
                current = current[path[i]];
            }
            current[path[path.length - 1]] = value;
        }
        onChange(newData);
    };

    const handleGoToProperties = () => {
        if (goToStep && goToStep('property')) {
            setTimeout(() => emitProbateScroll({ target: 'properties', focus: 'main_residence' }), 500);
            return;
        }
        if (goToStep && goToStep('review')) {
            setTimeout(() => emitProbateScroll({ target: 'properties', focus: 'main_residence' }), 500);
            return;
        }
        emitProbateScroll({ target: 'properties', focus: 'main_residence' });
    };

    const charityExists = data.iht_details?.charitable_bequests?.exists;
    const sp = data.iht_details?.predeceased_spouse;
    const ts = data.iht_details?.trust_screening;

    // Derive TNRB confidence whenever spouse data changes
    const tnrbConfidence: TNRBConfidence | null = sp?.exists ? deriveTNRBConfidence(sp) : null;

    // Auto-suggest 100% NRB if confirmed and estate was left entirely to spouse
    const suggestedNrbPercent = (
        sp?.exists &&
        sp?.estate_left_to_deceased === 'all' &&
        sp?.had_trusts_or_gifts === false
    ) ? 100 : null;

    // Suggested transferable RNRB % — offered click-to-apply (never silent).
    const suggestedRnrbPercent = sp?.exists ? deriveSuggestedRnrbPercent(sp) : null;
    // Residential Enhancement in force at the FIRST death — shown so the user
    // sees the year-specific denominator behind a partial-use derivation.
    const firstDeathRE = sp?.date_of_first_death ? residentialEnhancement(sp.date_of_first_death) : null;
    // Reasoning shown beside the suggestion so the user understands the basis.
    const rnrbSuggestReason = !sp?.date_of_first_death ? null
        : new Date(sp.date_of_first_death) < new Date('2017-04-06')
            ? 'first death before 6 April 2017 → no RNRB could have been used, so 100% transfers'
            : sp.first_spouse_estate_exempt === true
                ? 'first estate passed entirely to the spouse → RNRB unused'
                : sp.first_spouse_had_qualifying_residence === false
                    ? 'no residence in the first estate → RNRB unused'
                    : deriveRnrbPartialReason(sp)
                        ?? ((sp.first_estate_value != null && sp.first_estate_value > 2_000_000)
                            ? 'tapered because the first estate exceeded £2m'
                            : null);

    // Derive trust confidence and flags. `hasGWR` combines per-property GWR
    // (PropertyAsset.gift_with_reservation === 'yes') with the estate-level
    // trust_screening.has_gift_with_reservation flag, so a property-only signal
    // still flows through gwr_possible / gwr_likely / specialist_review_needed.
    const hasGWR = deriveHasGiftWithReservation(data, assets);
    const trustConfidence = ts ? deriveTrustConfidence(ts, hasGWR) : 'standard';
    const trustFlags = ts ? deriveTrustFlags(ts, hasGWR) : null;

    // Sync derived flags back into state when screening answers change
    const prevFlagsRef = useRef<string>('');
    useEffect(() => {
        if (!ts || !trustFlags) return;
        const flagsKey = JSON.stringify({
            ...trustFlags,
            confidence_level: trustConfidence,
        });
        if (flagsKey === prevFlagsRef.current) return;
        prevFlagsRef.current = flagsKey;

        // Only write back if values differ from current state
        const needsUpdate =
            ts.trust_involvement_detected !== trustFlags.trust_involvement_detected ||
            ts.trust_involvement_uncertain !== trustFlags.trust_involvement_uncertain ||
            ts.gwr_possible !== trustFlags.gwr_possible ||
            ts.gwr_likely !== trustFlags.gwr_likely ||
            ts.specialist_review_needed !== trustFlags.specialist_review_needed ||
            ts.has_trusts !== trustFlags.has_trusts ||
            ts.confidence_level !== trustConfidence;

        if (needsUpdate) {
            updateMany([
                [['iht_details', 'trust_screening', 'trust_involvement_detected'], trustFlags.trust_involvement_detected],
                [['iht_details', 'trust_screening', 'trust_involvement_uncertain'], trustFlags.trust_involvement_uncertain],
                [['iht_details', 'trust_screening', 'gwr_possible'], trustFlags.gwr_possible],
                [['iht_details', 'trust_screening', 'gwr_likely'], trustFlags.gwr_likely],
                [['iht_details', 'trust_screening', 'specialist_review_needed'], trustFlags.specialist_review_needed],
                [['iht_details', 'trust_screening', 'has_trusts'], trustFlags.has_trusts],
                [['iht_details', 'trust_screening', 'confidence_level'], trustConfidence],
            ]);
        }
    }, [ts?.created_or_transferred_trust, ts?.benefited_from_trust, ts?.has_gift_with_reservation,
        ts?.uncertain, ts?.trust_type, ts?.has_loan_trust_or_discounted_gift,
        ts?.gwr_market_rent_paid, ts?.gwr_continued_use]);

    return (
        <div className="max-w-2xl mx-auto space-y-8">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-ink">Who is inheriting?</h2>
                <p className="text-ink3 mt-2">This helps determine exemptions and tax allowances.</p>
            </div>

            {/* 1. Will */}
            <div className="bg-white p-6 rounded-xl border border-rule space-y-6">
                <div className="flex items-start gap-4">
                    <div className="p-2 bg-accent-soft rounded-lg text-accent">
                        <FileText className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                        <label className="block text-sm font-bold text-ink2 mb-2">Valid Will</label>
                        <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm text-ink3 cursor-pointer">
                                <input
                                    type="radio" name="will" value="yes"
                                    checked={data.representatives?.will_exists === 'yes'}
                                    onChange={() => update(['representatives', 'will_exists'], 'yes')}
                                />
                                Yes, there is a Will
                            </label>
                            <label className="flex items-center gap-2 text-sm text-ink3 cursor-pointer">
                                <input
                                    type="radio" name="will" value="no"
                                    checked={data.representatives?.will_exists === 'no'}
                                    onChange={() => update(['representatives', 'will_exists'], 'no')}
                                />
                                No (Intestate)
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. Descendants (RNRB) - Now handled in Assets */}

            {/* 3. Transferable Nil Rate Band — gated on Basics §1B answer.
                  - exists === false (Basics said No)  -> render a brief skip note
                  - exists === true                    -> render full block (legacy)
                  - exists_uncertain === true          -> full block + 'Not sure' nudge
                  - exists === null/undefined          -> full block (legacy entry) */}
            {sp?.exists === false ? (
                <div className="bg-white p-6 rounded-xl border border-rule">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-paper rounded-lg text-ink3">
                            <HeartHandshake className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-ink">Transferable Nil Rate Band</h3>
                            <p className="text-xs text-ink3 mt-1 leading-relaxed">
                                You indicated at the Basics step that no previous spouse or civil
                                partner predeceased them, so transferable allowance is not in play.
                                Edit the answer in Basics if you want to revisit this.
                            </p>
                        </div>
                    </div>
                </div>
            ) : (
            <div className="bg-white p-6 rounded-xl border border-rule animate-in fade-in slide-in-from-bottom-4">
                <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-accent-soft rounded-lg text-accent">
                        <HeartHandshake className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-ink">Transferable Nil Rate Band (TNRB)</h3>
                        <p className="text-xs text-ink3">Unused allowance from a pre-deceased spouse or civil partner (IHTA84 s8A)</p>
                    </div>
                </div>

                {sp?.exists_uncertain === true && (
                    <p className="font-serif italic text-[13px] text-ink2 leading-relaxed pl-4 border-l-2 border-accent mb-4">
                        You answered "Not sure" at the Basics step. Anything you confirm here will
                        sharpen the audit; otherwise we'll keep the flag.
                    </p>
                )}

                <div className="bg-paper p-4 rounded-lg border border-rule-soft space-y-4">
                    <p className="text-sm font-medium text-ink2">
                        Was the deceased widowed (did they have a spouse or civil partner who died before them)?
                    </p>

                    <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer p-3 bg-white rounded border border-rule hover:border-accent hover:ring-1 hover:ring-accent transition-all w-full md:w-auto">
                            <input
                                type="radio"
                                name="has_late_spouse_ben"
                                className="text-accent focus:ring-accent"
                                checked={sp?.exists === true}
                                onChange={() => update(['iht_details', 'predeceased_spouse', 'exists'], true)}
                            />
                            <span className="text-sm font-bold text-ink2">Yes</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer p-3 bg-white rounded border border-rule hover:border-accent hover:ring-1 hover:ring-accent transition-all w-full md:w-auto">
                            <input
                                type="radio"
                                name="has_late_spouse_ben"
                                className="text-ink3 focus:ring-ink2"
                                /* sp.exists === false is handled by the outer ternary
                                   (skip-note branch); inside this branch the No radio
                                   is a control to flip the answer, never a state. */
                                checked={false}
                                onChange={() => {
                                    updateMany([
                                        [['iht_details', 'predeceased_spouse', 'exists'], false],
                                        [['iht_details', 'predeceased_spouse', 'unused_nrb_percent'], null],
                                        [['iht_details', 'predeceased_spouse', 'unused_rnrb_percent'], null],
                                        [['iht_details', 'predeceased_spouse', 'trnrb_claim'], null],
                                        [['iht_details', 'predeceased_spouse', 'date_of_first_death'], null],
                                        [['iht_details', 'predeceased_spouse', 'estate_left_to_deceased'], null],
                                        [['iht_details', 'predeceased_spouse', 'had_trusts_or_gifts'], null],
                                        [['iht_details', 'predeceased_spouse', 'records_available'], null],
                                        [['iht_details', 'predeceased_spouse', 'confidence'], null],
                                        [['iht_details', 'predeceased_spouse', 'manual_override'], null],
                                    ]);
                                }}
                            />
                            <span className="text-sm font-bold text-ink2">No</span>
                        </label>
                    </div>

                    {sp?.exists === true && (
                        <div className="mt-2 pt-4 border-t border-rule animate-in fade-in slide-in-from-top-2 space-y-5">
                            {/* Guided questions */}
                            <div className="space-y-4">
                                {/* Q1: Date of first death */}
                                <div>
                                    <label className="block text-xs font-bold text-ink3 mb-1">
                                        When did the first spouse/civil partner die?
                                    </label>
                                    <input
                                        type="date"
                                        className="w-full md:w-56 text-sm border rounded-lg p-2 focus:ring-2 focus:ring-accent/20 focus:outline-none"
                                        value={sp?.date_of_first_death ?? ''}
                                        onChange={e => update(['iht_details', 'predeceased_spouse', 'date_of_first_death'], e.target.value || null)}
                                    />
                                    <p className="text-[10px] text-ink3 mt-1">
                                        The percentage of unused NRB is based on the threshold at the time of the first death.
                                    </p>
                                </div>

                                {/* Q2: How much was left to the deceased? */}
                                <div>
                                    <label className="block text-xs font-bold text-ink3 mb-1.5">
                                        How was the first spouse's estate distributed?
                                    </label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {([
                                            ['all', 'Everything left to the deceased'],
                                            ['most', 'Most left to the deceased'],
                                            ['partial', 'Partly left to others (children, etc.)'],
                                            ['none', 'Nothing left to the deceased'],
                                            ['unknown', 'Not sure / no records'],
                                        ] as const).map(([val, label]) => (
                                            <label key={val} className={`flex items-center gap-2 cursor-pointer p-2.5 rounded border text-xs transition-all ${sp?.estate_left_to_deceased === val
                                                ? 'bg-accent-soft border-accent ring-1 ring-accent font-bold text-accent'
                                                : 'bg-white border-rule hover:border-accent text-ink2'
                                                }`}>
                                                <input
                                                    type="radio"
                                                    name="estate_left_to"
                                                    className="text-accent focus:ring-accent"
                                                    checked={sp?.estate_left_to_deceased === val}
                                                    onChange={() => {
                                                        update(['iht_details', 'predeceased_spouse', 'estate_left_to_deceased'], val);
                                                        // Auto-suggest percentage
                                                        if (val === 'all' && sp?.had_trusts_or_gifts === false) {
                                                            update(['iht_details', 'predeceased_spouse', 'unused_nrb_percent'], 100);
                                                        } else if (val === 'none') {
                                                            update(['iht_details', 'predeceased_spouse', 'unused_nrb_percent'], 0);
                                                        }
                                                    }}
                                                />
                                                {label}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Q3: Were there trusts or lifetime gifts? */}
                                {sp?.estate_left_to_deceased && sp.estate_left_to_deceased !== 'none' && (
                                    <div className="animate-in fade-in slide-in-from-top-2">
                                        <label className="block text-xs font-bold text-ink3 mb-1.5">
                                            Did the first spouse create any trusts or make significant lifetime gifts?
                                        </label>
                                        <p className="text-[10px] text-ink3 mb-2">
                                            Trusts or gifts made by the first spouse may have used some of their Nil Rate Band.
                                        </p>
                                        <div className="flex gap-3">
                                            <label className={`flex items-center gap-2 cursor-pointer p-2.5 rounded border text-xs transition-all ${sp?.had_trusts_or_gifts === true
                                                ? 'bg-[#FAF3E0] border-[#E8D89A] ring-1 ring-[#E8D89A] font-bold text-[#7A5A00]'
                                                : 'bg-white border-rule hover:border-accent text-ink2'
                                                }`}>
                                                <input
                                                    type="radio" name="had_trusts"
                                                    className="text-accent focus:ring-accent"
                                                    checked={sp?.had_trusts_or_gifts === true}
                                                    onChange={() => update(['iht_details', 'predeceased_spouse', 'had_trusts_or_gifts'], true)}
                                                />
                                                Yes
                                            </label>
                                            <label className={`flex items-center gap-2 cursor-pointer p-2.5 rounded border text-xs transition-all ${sp?.had_trusts_or_gifts === false
                                                ? 'bg-paper border-rule ring-1 ring-rule font-bold text-ink'
                                                : 'bg-white border-rule hover:border-accent text-ink2'
                                                }`}>
                                                <input
                                                    type="radio" name="had_trusts"
                                                    className="text-accent focus:ring-accent"
                                                    checked={sp?.had_trusts_or_gifts === false}
                                                    onChange={() => {
                                                        update(['iht_details', 'predeceased_spouse', 'had_trusts_or_gifts'], false);
                                                        // If estate was left entirely to deceased and no trusts → auto-suggest 100%
                                                        if (sp?.estate_left_to_deceased === 'all') {
                                                            update(['iht_details', 'predeceased_spouse', 'unused_nrb_percent'], 100);
                                                        }
                                                    }}
                                                />
                                                No
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {/* Q4: Records available */}
                                {sp?.estate_left_to_deceased && sp.estate_left_to_deceased !== 'none' && (
                                    <div className="animate-in fade-in slide-in-from-top-2">
                                        <label className="block text-xs font-bold text-ink3 mb-1.5">
                                            What records are available from the first death?
                                        </label>
                                        <div className="flex gap-3">
                                            {([
                                                ['full', 'Full records (grant, accounts, IHT return)'],
                                                ['partial', 'Some records'],
                                                ['none', 'No records available'],
                                            ] as const).map(([val, label]) => (
                                                <label key={val} className={`flex items-center gap-2 cursor-pointer p-2.5 rounded border text-xs transition-all ${sp?.records_available === val
                                                    ? 'bg-accent-soft border-accent ring-1 ring-accent font-bold text-accent'
                                                    : 'bg-white border-rule hover:border-accent text-ink2'
                                                    }`}>
                                                    <input
                                                        type="radio" name="records_avail"
                                                        className="text-accent focus:ring-accent"
                                                        checked={sp?.records_available === val}
                                                        onChange={() => update(['iht_details', 'predeceased_spouse', 'records_available'], val)}
                                                    />
                                                    {label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Confidence indicator */}
                            {tnrbConfidence && (
                                <div className={`rounded-lg p-3 text-xs border ${tnrbConfidence === 'confirmed'
                                    ? 'bg-paper border-rule text-ink'
                                    : tnrbConfidence === 'possible'
                                        ? 'bg-[#FAF3E0] border-[#E8D89A] text-[#7A5A00]'
                                        : 'bg-paper border-rule text-ink3'
                                    }`}>
                                    <div className="flex items-center gap-1.5 font-bold mb-1">
                                        {tnrbConfidence === 'confirmed' && <><CheckCircle2 className="w-3.5 h-3.5" /> TNRB transfer likely available</>}
                                        {tnrbConfidence === 'possible' && <><HelpCircle className="w-3.5 h-3.5" /> TNRB transfer may be available</>}
                                        {tnrbConfidence === 'none' && <><Info className="w-3.5 h-3.5" /> No transferable NRB expected</>}
                                    </div>
                                    {tnrbConfidence === 'confirmed' && (
                                        <p>Based on the information provided, the full NRB from the first spouse appears to be available for transfer. HMRC form IHT402 will be required.</p>
                                    )}
                                    {tnrbConfidence === 'possible' && (
                                        <p>A transfer may be possible but the exact percentage is uncertain. Enter the percentage below if known, or seek professional advice. HMRC form IHT402 will be needed to claim.</p>
                                    )}
                                    {tnrbConfidence === 'none' && (
                                        <p>The first spouse's NRB appears to have been fully used. No transfer is expected.</p>
                                    )}
                                </div>
                            )}

                            {/* Percentage inputs. Shown whenever there is a predeceased spouse:
                                NRB state must not control whether TRNRB facts can be supplied (a first
                                estate that used all its NRB may still have unused RNRB, e.g. 100% for a
                                first death before 6 April 2017). Only the NRB input follows NRB confidence. */}
                            {tnrbConfidence && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 animate-in fade-in slide-in-from-top-2">
                                    {tnrbConfidence !== 'none' && (
                                    <div>
                                        <label className="flex items-center gap-1 text-[10px] uppercase font-bold text-ink3 mb-1">
                                            Unused NRB (%)
                                            <HelpPopover content={HELP_CONTENT.NRB} />
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="number" className="w-full text-sm border rounded-lg p-2 pr-8 focus:ring-2 focus:ring-accent/20" placeholder="0 - 100"
                                                min="0" max="100"
                                                value={sp?.unused_nrb_percent ?? ''}
                                                onChange={e => {
                                                    const val = Math.min(100, Math.max(0, Number(e.target.value)));
                                                    updateMany([
                                                        [['iht_details', 'predeceased_spouse', 'unused_nrb_percent'], val],
                                                        [['iht_details', 'predeceased_spouse', 'manual_override'], true],
                                                        [['iht_details', 'predeceased_spouse', 'confidence'], deriveTNRBConfidence({ ...sp!, unused_nrb_percent: val, manual_override: true })],
                                                    ]);
                                                }}
                                            />
                                            <span className="absolute right-3 top-2 text-ink3 text-sm">%</span>
                                        </div>
                                        {suggestedNrbPercent === 100 && (sp?.unused_nrb_percent == null || sp?.unused_nrb_percent === 0) && (
                                            <p className="text-[10px] text-accent mt-1">
                                                Based on your answers, 100% may apply.{' '}
                                                <button
                                                    type="button"
                                                    className="underline font-bold"
                                                    onClick={() => update(['iht_details', 'predeceased_spouse', 'unused_nrb_percent'], 100)}
                                                >
                                                    Use 100%
                                                </button>
                                            </p>
                                        )}
                                    </div>
                                    )}
                                    <div>
                                        <label className="flex items-center gap-1 text-[10px] uppercase font-bold text-ink3 mb-1">
                                            Unused RNRB (%)
                                            <HelpPopover content={HELP_CONTENT.RNRB} />
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="number" className="w-full text-sm border rounded-lg p-2 pr-8 focus:ring-2 focus:ring-accent/20" placeholder="0 - 100"
                                                min="0" max="100"
                                                value={sp?.unused_rnrb_percent ?? ''}
                                                onChange={e => update(['iht_details', 'predeceased_spouse', 'unused_rnrb_percent'], e.target.value === '' ? null : Math.min(100, Math.max(0, Number(e.target.value))))}
                                            />
                                            <span className="absolute right-3 top-2 text-ink3 text-sm">%</span>
                                        </div>
                                        {suggestedRnrbPercent != null && suggestedRnrbPercent > 0 && (sp?.unused_rnrb_percent == null || sp?.unused_rnrb_percent === 0) && (
                                            <p className="text-[10px] text-accent mt-1">
                                                Based on your answers, {suggestedRnrbPercent}% may transfer{rnrbSuggestReason ? ` (${rnrbSuggestReason})` : ''}.{' '}
                                                <button
                                                    type="button"
                                                    className="underline font-bold"
                                                    onClick={() => update(['iht_details', 'predeceased_spouse', 'unused_rnrb_percent'], suggestedRnrbPercent)}
                                                >
                                                    Use {suggestedRnrbPercent}%
                                                </button>
                                            </p>
                                        )}
                                        <p className="text-[10px] text-ink3 mt-1">
                                            A first death before 6 April 2017 still transfers 100% (no RNRB could have been used then). Form IHT436 claims it.
                                        </p>
                                        <div className="mt-2">
                                            <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">
                                                First spouse's estate value (only if over £2m)
                                            </label>
                                            <input
                                                type="number" className="w-full text-sm border rounded-lg p-2 focus:ring-2 focus:ring-accent/20" placeholder="Leave blank if under £2m"
                                                min="0"
                                                value={sp?.first_estate_value ?? ''}
                                                onChange={e => update(['iht_details', 'predeceased_spouse', 'first_estate_value'], e.target.value ? Number(e.target.value) : null)}
                                            />
                                            <p className="text-[10px] text-ink3 mt-1">Only needed to taper the transfer if the first estate exceeded £2m.</p>
                                        </div>
                                        {/* s.8L claim — the transferred RNRB is nil unless claimed (IHTA 1984 s.8G(3)). Never defaulted. */}
                                        <div className="mt-3">
                                            <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">
                                                Is a claim for the transferable RNRB being made (form IHT436)?
                                            </label>
                                            <div className="flex gap-2">
                                                {(['yes', 'no', 'unsure'] as const).map(val => (
                                                    <label key={val} className={`flex items-center gap-1.5 cursor-pointer p-1.5 rounded border text-xs transition-all ${sp?.trnrb_claim === val
                                                        ? 'bg-accent-soft border-accent ring-1 ring-accent font-bold text-accent'
                                                        : 'bg-white border-rule hover:border-accent text-ink2'
                                                        }`}>
                                                        <input
                                                            type="radio" name="trnrb_claim"
                                                            className="text-accent focus:ring-accent"
                                                            checked={sp?.trnrb_claim === val}
                                                            onChange={() => update(['iht_details', 'predeceased_spouse', 'trnrb_claim'], val)}
                                                        />
                                                        {val === 'yes' ? 'Yes' : val === 'no' ? 'No' : 'Not sure'}
                                                    </label>
                                                ))}
                                            </div>
                                            <p className="text-[10px] text-ink3 mt-1">
                                                The transferred RNRB only counts if it is claimed. Until you answer Yes, it is left out of the estimate.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* TRNRB — Transferable Residence Nil Rate Band */}
                            {sp?.date_of_first_death && new Date(sp.date_of_first_death) >= new Date('2017-04-06') && (
                                <div className="pt-4 border-t border-accent space-y-4 animate-in fade-in slide-in-from-top-2">
                                    <div>
                                        <label className="block text-xs font-bold text-accent mb-1">
                                            Transferable Residence Nil Rate Band (TRNRB)
                                        </label>
                                        <p className="text-[10px] text-ink3 mb-3">
                                            If the first spouse died on or after 6 April 2017, unused RNRB may be transferable.
                                        </p>
                                    </div>

                                    {/* TRNRB Q1: Did first spouse have qualifying residence? */}
                                    <div>
                                        <label className="block text-xs font-bold text-ink3 mb-1.5">
                                            Did the first spouse's estate include a qualifying residence?
                                        </label>
                                        <div className="flex gap-3">
                                            {(['yes', 'no', 'unsure'] as const).map(val => (
                                                <label key={val} className={`flex items-center gap-2 cursor-pointer p-2 rounded border text-xs transition-all ${(val === 'yes' && sp?.first_spouse_had_qualifying_residence === true) || (val === 'no' && sp?.first_spouse_had_qualifying_residence === false) || (val === 'unsure' && sp?.first_spouse_had_qualifying_residence === null && sp?.first_spouse_had_qualifying_residence !== undefined)
                                                    ? 'bg-accent-soft border-accent ring-1 ring-accent font-bold text-accent'
                                                    : 'bg-white border-rule hover:border-accent text-ink2'
                                                    }`}>
                                                    <input
                                                        type="radio" name="trnrb_q1"
                                                        className="text-accent focus:ring-accent"
                                                        checked={val === 'yes' ? sp?.first_spouse_had_qualifying_residence === true : val === 'no' ? sp?.first_spouse_had_qualifying_residence === false : false}
                                                        onChange={() => update(['iht_details', 'predeceased_spouse', 'first_spouse_had_qualifying_residence'], val === 'yes' ? true : val === 'no' ? false : null)}
                                                    />
                                                    {val === 'yes' ? 'Yes' : val === 'no' ? 'No' : 'Not sure'}
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* TRNRB Q2: Did it pass to direct descendants? */}
                                    {sp?.first_spouse_had_qualifying_residence === true && (
                                        <div className="animate-in fade-in slide-in-from-top-2">
                                            <label className="block text-xs font-bold text-ink3 mb-1.5">
                                                Did the qualifying residence pass to direct descendants?
                                            </label>
                                            <div className="flex gap-3">
                                                {(['yes', 'no', 'unsure'] as const).map(val => (
                                                    <label key={val} className={`flex items-center gap-2 cursor-pointer p-2 rounded border text-xs transition-all ${(val === 'yes' && sp?.first_spouse_residence_to_descendants === true) || (val === 'no' && sp?.first_spouse_residence_to_descendants === false)
                                                        ? 'bg-accent-soft border-accent ring-1 ring-accent font-bold text-accent'
                                                        : 'bg-white border-rule hover:border-accent text-ink2'
                                                        }`}>
                                                        <input
                                                            type="radio" name="trnrb_q2"
                                                            className="text-accent focus:ring-accent"
                                                            checked={val === 'yes' ? sp?.first_spouse_residence_to_descendants === true : val === 'no' ? sp?.first_spouse_residence_to_descendants === false : false}
                                                            onChange={() => update(['iht_details', 'predeceased_spouse', 'first_spouse_residence_to_descendants'], val === 'yes' ? true : val === 'no' ? false : null)}
                                                        />
                                                        {val === 'yes' ? 'Yes' : val === 'no' ? 'No' : 'Not sure'}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* TRNRB partial-use capture — when the residence passed to
                                        descendants, SOME RNRB was used. We capture the residence
                                        VALUE (and over-£2m estate value) and DERIVE the unused %,
                                        rather than make a layperson run a year-specific calculation. */}
                                    {sp?.first_spouse_residence_to_descendants === true && (
                                        <div className="animate-in fade-in slide-in-from-top-2 bg-paper p-3 rounded-lg border border-rule-soft space-y-3">
                                            <p className="text-[11px] text-ink2 leading-relaxed">
                                                Some RNRB was used on the first death. Enter the value of the residence that
                                                passed to direct descendants — we'll work out the unused percentage for you.
                                                It's measured against the Residence Nil Rate Band in force at the{' '}
                                                <strong>first</strong> death
                                                {firstDeathRE != null ? <> (£{firstDeathRE.toLocaleString()} for that tax year)</> : null},
                                                not today's £175,000.
                                            </p>
                                            <div>
                                                <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">
                                                    Value of residence passing to direct descendants (£)
                                                </label>
                                                <input
                                                    type="number" min="0"
                                                    className="w-full md:w-64 text-sm border rounded-lg p-2 focus:ring-2 focus:ring-accent/20"
                                                    placeholder="e.g. 75000"
                                                    value={sp?.first_spouse_residence_to_descendants_value ?? ''}
                                                    onChange={e => update(['iht_details', 'predeceased_spouse', 'first_spouse_residence_to_descendants_value'], e.target.value ? Number(e.target.value) : null)}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">
                                                    First spouse's estate value (only if over £2m)
                                                </label>
                                                <input
                                                    type="number" min="0"
                                                    className="w-full md:w-64 text-sm border rounded-lg p-2 focus:ring-2 focus:ring-accent/20"
                                                    placeholder="Leave blank if under £2m"
                                                    value={sp?.first_estate_value ?? ''}
                                                    onChange={e => update(['iht_details', 'predeceased_spouse', 'first_estate_value'], e.target.value ? Number(e.target.value) : null)}
                                                />
                                                <p className="text-[10px] text-ink3 mt-1">Needed only to taper the allowance if the first estate exceeded £2m.</p>
                                            </div>
                                            {/* Derived suggestion — surfaced with its reasoning, never silently applied. */}
                                            {sp?.first_spouse_residence_to_descendants_value != null && suggestedRnrbPercent != null && (
                                                suggestedRnrbPercent > 0 ? (
                                                    <div className="text-[11px] text-accent bg-accent-soft p-2 rounded border border-accent">
                                                        Derived unused RNRB: <strong>{suggestedRnrbPercent}%</strong>
                                                        {rnrbSuggestReason ? <> ({rnrbSuggestReason})</> : null}.{' '}
                                                        {sp?.unused_rnrb_percent === suggestedRnrbPercent ? (
                                                            <span className="font-bold">Applied.</span>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="underline font-bold"
                                                                onClick={() => update(['iht_details', 'predeceased_spouse', 'unused_rnrb_percent'], suggestedRnrbPercent)}
                                                            >
                                                                Use {suggestedRnrbPercent}%
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="text-[11px] text-ink3 bg-paper p-2 rounded border border-rule">
                                                        The residence used the full RNRB available on the first death — there is no unused RNRB to transfer (0%).
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    )}

                                    {/* TRNRB Q3: Was the first estate exempt? */}
                                    <div>
                                        <label className="block text-xs font-bold text-ink3 mb-1.5">
                                            Was the first spouse's estate exempt because everything passed to the surviving spouse?
                                        </label>
                                        <p className="text-[10px] text-ink3 mb-2">
                                            If yes, the entire RNRB may be available for transfer (it was preserved, not used).
                                        </p>
                                        <div className="flex gap-3">
                                            {(['yes', 'no', 'unsure'] as const).map(val => (
                                                <label key={val} className={`flex items-center gap-2 cursor-pointer p-2 rounded border text-xs transition-all ${(val === 'yes' && sp?.first_spouse_estate_exempt === true) || (val === 'no' && sp?.first_spouse_estate_exempt === false)
                                                    ? 'bg-accent-soft border-accent ring-1 ring-accent font-bold text-accent'
                                                    : 'bg-white border-rule hover:border-accent text-ink2'
                                                    }`}>
                                                    <input
                                                        type="radio" name="trnrb_q3"
                                                        className="text-accent focus:ring-accent"
                                                        checked={val === 'yes' ? sp?.first_spouse_estate_exempt === true : val === 'no' ? sp?.first_spouse_estate_exempt === false : false}
                                                        onChange={() => update(['iht_details', 'predeceased_spouse', 'first_spouse_estate_exempt'], val === 'yes' ? true : val === 'no' ? false : null)}
                                                    />
                                                    {val === 'yes' ? 'Yes' : val === 'no' ? 'No' : 'Not sure'}
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* TRNRB confidence indicator */}
                                    {(() => {
                                        const tConf = deriveTRNRBConfidence(sp!);
                                        if (!tConf) return null;
                                        return (
                                            <div className={`rounded-lg p-3 text-xs border ${tConf === 'confirmed'
                                                ? 'bg-paper border-rule text-ink'
                                                : tConf === 'possible'
                                                    ? 'bg-[#FAF3E0] border-[#E8D89A] text-[#7A5A00]'
                                                    : 'bg-paper border-rule text-ink3'
                                                }`}>
                                                <div className="flex items-center gap-1.5 font-bold mb-1">
                                                    {tConf === 'confirmed' && <><CheckCircle2 className="w-3.5 h-3.5" /> Additional RNRB transfer likely available</>}
                                                    {tConf === 'possible' && <><HelpCircle className="w-3.5 h-3.5" /> Additional RNRB transfer may be available</>}
                                                    {tConf === 'none' && <><Info className="w-3.5 h-3.5" /> No transferable RNRB expected</>}
                                                </div>
                                                {tConf === 'confirmed' && (
                                                    <p>An additional Residence Nil Rate Band appears to be available from the previously deceased spouse or civil partner. Enter the unused RNRB percentage above. HMRC form IHT436 will be required.</p>
                                                )}
                                                {tConf === 'possible' && (
                                                    <p>You may be able to claim an additional Residence Nil Rate Band from the previously deceased spouse. This could reduce the estimated tax, but the first estate needs to be confirmed.</p>
                                                )}
                                                {tConf === 'none' && (
                                                    <p>Based on the information provided, no transferable RNRB is expected from the first spouse's estate.</p>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* Info box */}
                            <div className="text-[10px] text-accent bg-accent-soft p-2 rounded border border-accent">
                                The transferable NRB (TNRB) allows unused Nil Rate Band from a predeceased spouse to be added to the current estate's allowance. The percentage transferred is based on how much NRB was unused at the first death. HMRC form IHT402 is required to make this claim.
                            </div>
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* 4. Charitable Bequests */}
            <div className="bg-white p-6 rounded-xl border border-rule animate-in fade-in slide-in-from-bottom-4">
                <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-paper rounded-lg text-ink2">
                        <Heart className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-ink">Charitable Gifts</h3>
                        <p className="text-xs text-ink3">Gifts to charity may reduce the IHT rate from 40% to 36%</p>
                    </div>
                </div>

                <div className="bg-paper p-4 rounded-lg border border-rule-soft">
                    <p className="text-sm font-medium text-ink2 mb-3">
                        Does the estate include any gifts to a registered charity?
                    </p>
                    <div className="flex gap-4 mb-2">
                        <label className="flex items-center gap-2 cursor-pointer p-3 bg-white rounded border border-rule hover:border-rule hover:ring-1 hover:ring-rule transition-all w-full md:w-auto">
                            <input
                                type="radio"
                                name="charity_exists"
                                className="text-ink2 focus:ring-ink2"
                                checked={charityExists === true}
                                onChange={() => update(['iht_details', 'charitable_bequests', 'exists'], true)}
                            />
                            <span className="text-sm font-bold text-ink2">Yes</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer p-3 bg-white rounded border border-rule hover:border-rule transition-all w-full md:w-auto">
                            <input
                                type="radio"
                                name="charity_exists"
                                className="text-ink3 focus:ring-ink2"
                                checked={charityExists === false}
                                onChange={() => {
                                    update(['iht_details', 'charitable_bequests', 'exists'], false);
                                    update(['iht_details', 'charitable_bequests', 'total_value'], null);
                                }}
                            />
                            <span className="text-sm font-bold text-ink2">No</span>
                        </label>
                    </div>

                    {charityExists === true && (
                        <div className="mt-4 pt-4 border-t border-rule animate-in fade-in slide-in-from-top-2 space-y-3">
                            <div>
                                <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">
                                    Total value going to charity (£)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    className="w-full text-sm border rounded-lg p-2 focus:ring-2 focus:ring-ink2/20 focus:outline-none"
                                    placeholder="e.g. 50000"
                                    value={data.iht_details?.charitable_bequests?.total_value ?? ''}
                                    onChange={e => update(['iht_details', 'charitable_bequests', 'total_value'],
                                        e.target.value ? Number(e.target.value) : null)}
                                />
                            </div>
                            <div className="text-xs text-ink2 bg-paper p-2 rounded border border-rule">
                                If at least 10% of the taxable estate passes to charity, the IHT rate reduces from <strong>40%</strong> to <strong>36%</strong> (IHTA84 s7(2)).
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* 5. Downsizing Allowance (Enhanced) */}
            <div className="bg-white p-6 rounded-xl border border-rule animate-in fade-in slide-in-from-bottom-4">
                <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-[#F5E8C4] rounded-lg text-[#7A5A00]">
                        <TrendingDown className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-ink">RNRB Downsizing Relief</h3>
                        <p className="text-xs text-ink3">Claim RNRB for a former home sold or given away on or after 8 July 2015 (IHTM46055)</p>
                    </div>
                </div>

                <div className="bg-paper p-4 rounded-lg border border-rule-soft" data-testid="former-home-question">
                    <p className="text-sm font-medium text-ink2 mb-1">
                        {FORMER_HOME_QUESTION(formerHomeName)}
                    </p>
                    <p className="text-[11px] text-ink2 mb-1">{FORMER_HOME_QUALIFIER}</p>
                    <p className="text-[10px] text-ink3 mb-3">{FORMER_HOME_HELP}</p>
                    {legacyDownsizingRecorded && (
                        <p className="text-[11px] text-[#7A5A00] bg-[#FAF3E0] p-2 rounded border border-[#E8D89A] mb-3" data-testid="former-home-legacy-note">
                            An earlier version of this tool recorded that a qualifying home was sold or given away. Please answer this question to confirm it; until you do, the earlier answer and figures are still used.
                        </p>
                    )}

                    <div className="flex gap-4 mb-2" role="radiogroup">
                        {FORMER_HOME_OPTIONS.map((opt) => (
                            <label key={opt.value} className="flex items-center gap-2 cursor-pointer p-3 bg-white rounded border border-rule hover:border-[#E8D89A] hover:ring-1 hover:ring-[#E8D89A] transition-all w-full md:w-auto">
                                <input
                                    type="radio"
                                    name="former_residence_disposed"
                                    value={opt.value}
                                    className="text-[#7A5A00] focus:ring-[#7A5A00]"
                                    checked={formerHome === opt.value}
                                    onChange={() => setFormerHome(opt.value)}
                                />
                                <span className="text-sm font-bold text-ink2">{opt.label}</span>
                            </label>
                        ))}
                    </div>

                    {/* The downsizing figures are asked only once a disposal is answered 'yes'. A disposal is not a claim: the
                        s.8L claim stays its own question below. */}
                    {formerHome === 'yes' && (
                        <div className="mt-4 pt-4 border-t border-rule animate-in fade-in slide-in-from-top-2 space-y-4">
                            {!formerDisposals && (<>
                            <FormerDisposalKindQuestion name="former_disposal_kind" value={downsizing.disposal_kind}
                                onChange={(v) => update(['iht_details', 'downsizing_allowance', 'disposal_kind'], v)} />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">
                                        Date of disposal
                                    </label>
                                    <input
                                        type="date"
                                        min="2015-07-08"
                                        className="w-full text-sm border rounded-lg p-2 focus:ring-2 focus:ring-[#7A5A00]/20 focus:outline-none"
                                        value={data.iht_details?.downsizing_allowance?.disposal_date ?? ''}
                                        onChange={e => update(['iht_details', 'downsizing_allowance', 'disposal_date'],
                                            e.target.value || null)}
                                    />
                                    {data.iht_details?.downsizing_allowance?.disposal_date && new Date(data.iht_details.downsizing_allowance.disposal_date) < new Date('2015-07-08') && (
                                        <p className="text-[10px] text-ink2 mt-1 font-medium">
                                            Downsizing relief only applies to disposals on or after 8 July 2015.
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">
                                        Value of the whole former home at disposal date (£)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        className="w-full text-sm border rounded-lg p-2 focus:ring-2 focus:ring-[#7A5A00]/20 focus:outline-none"
                                        placeholder="e.g. 350000"
                                        value={data.iht_details?.downsizing_allowance?.former_property_value ?? ''}
                                        onChange={e => update(['iht_details', 'downsizing_allowance', 'former_property_value'],
                                            e.target.value ? Number(e.target.value) : null)}
                                    />
                                </div>
                            </div>
                            {/* D35: s.8FE(2) values the deceased's interest, so their share is asked, never assumed to be 100%. */}
                            <div>
                                <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">
                                    Deceased's share of the former home (%)
                                </label>
                                <input
                                    type="number"
                                    min="0.01"
                                    max="100"
                                    step="any"
                                    aria-label="Deceased's share of the former home (%)"
                                    className="w-full md:w-1/2 text-sm border rounded-lg p-2 focus:ring-2 focus:ring-[#7A5A00]/20 focus:outline-none"
                                    placeholder="e.g. 100"
                                    value={data.iht_details?.downsizing_allowance?.former_property_share_percent ?? ''}
                                    onChange={e => update(['iht_details', 'downsizing_allowance', 'former_property_share_percent'],
                                        e.target.value ? Number(e.target.value) : null)}
                                />
                                <p className="text-[10px] text-ink3 mt-1">
                                    Enter 100 if they owned all of it. If they owned it with someone else, enter their share when it was sold or given away (for example 50 for an equal share with one other person). The addition is worked out on the value of their share.
                                </p>
                            </div>
                            </>)}
                            {/* D42: several disposals (one entry per home per day) and the PRs' nomination of one. */}
                            {formerDisposals && (
                                <div className="space-y-3" data-testid="former-disposals">
                                    <p className="text-[10px] text-ink3">{FORMER_DISPOSALS_HELP}</p>
                                    {formerDisposals.map((d, i) => (
                                        <div key={String(d.id ?? i)} className="p-3 rounded-lg border border-rule bg-white space-y-3" data-testid="former-disposal">
                                            <div className="flex items-center justify-between gap-2">
                                                <input
                                                    type="text"
                                                    aria-label="Which home (address or a short name)"
                                                    placeholder={`Home ${i + 1} — address or a short name`}
                                                    className="flex-1 text-sm border rounded-lg p-2 focus:ring-2 focus:ring-[#7A5A00]/20 focus:outline-none"
                                                    value={typeof d.label === 'string' ? d.label : ''}
                                                    onChange={e => updateFormerDisposal(d.id, 'label', e.target.value || null)}
                                                />
                                                <button type="button" className="text-xs text-ink3 hover:text-ink2 underline" onClick={() => removeFormerDisposal(i)}>Remove</button>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">Date of disposal</label>
                                                    <input type="date" min="2015-07-08" aria-label={`Date of disposal, home ${i + 1}`}
                                                        className="w-full text-sm border rounded-lg p-2 focus:ring-2 focus:ring-[#7A5A00]/20 focus:outline-none"
                                                        value={typeof d.disposal_date === 'string' ? d.disposal_date : ''}
                                                        onChange={e => updateFormerDisposal(d.id, 'disposal_date', e.target.value || null)} />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">Value of the whole home at disposal date (£)</label>
                                                    <input type="number" min="0" aria-label={`Value of the whole home, home ${i + 1}`}
                                                        className="w-full text-sm border rounded-lg p-2 focus:ring-2 focus:ring-[#7A5A00]/20 focus:outline-none"
                                                        value={typeof d.former_property_value === 'number' ? d.former_property_value : ''}
                                                        onChange={e => updateFormerDisposal(d.id, 'former_property_value', e.target.value ? Number(e.target.value) : null)} />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">Deceased's share disposed of (%)</label>
                                                    <input type="number" min="0.01" max="100" step="any" aria-label={`Deceased's share disposed of (%), home ${i + 1}`}
                                                        className="w-full text-sm border rounded-lg p-2 focus:ring-2 focus:ring-[#7A5A00]/20 focus:outline-none"
                                                        value={typeof d.former_property_share_percent === 'number' ? d.former_property_share_percent : ''}
                                                        onChange={e => updateFormerDisposal(d.id, 'former_property_share_percent', e.target.value ? Number(e.target.value) : null)} />
                                                </div>
                                            </div>
                                            <div>
                                                <p className="text-[11px] font-bold text-ink2 mb-1">{FORMER_DISPOSAL_LIVED_IN(formerHomeName)}</p>
                                                <div className="flex gap-2" role="radiogroup">
                                                    {FORMER_HOME_OPTIONS.map((opt) => (
                                                        <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer p-1.5 rounded border border-rule text-xs">
                                                            <input type="radio" name={`former_disposal_lived_in_${String(d.id ?? i)}`} value={opt.value}
                                                                checked={d.lived_in === opt.value}
                                                                onChange={() => updateFormerDisposal(d.id, 'lived_in', opt.value)} />
                                                            {opt.label}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                            <FormerDisposalKindQuestion name={`former_disposal_kind_${String(d.id ?? i)}`} value={d.disposal_kind}
                                                onChange={(v) => updateFormerDisposal(d.id, 'disposal_kind', v)} />
                                        </div>
                                    ))}
                                    {/* D42 same-day check: asked beside the entries, before any nomination. */}
                                    {formerSameDay.groups.map((g) => (
                                        <div key={g.date} className="p-3 rounded-lg border border-[#E8D89A] bg-[#FAF3E0]" data-testid="former-disposal-same-day">
                                            <p className="text-sm font-bold text-ink2 mb-1">{FORMER_DISPOSAL_SAME_DAY_QUESTION}</p>
                                            <ul className="text-xs text-ink2 mb-2 list-disc pl-5">
                                                {g.entries.map((d) => <li key={String(d.id)}>{formerDisposalLabel(d, (formerDisposals ?? []).indexOf(d))}</li>)}
                                            </ul>
                                            {g.ids && (
                                                <div className="flex flex-wrap gap-2" role="radiogroup">
                                                    {FORMER_DISPOSAL_SAME_DAY_OPTIONS.map((opt) => (
                                                        <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer p-1.5 rounded border border-rule bg-white text-xs">
                                                            <input type="radio" name={`former_disposal_same_day_${g.date}`} value={opt.value}
                                                                checked={g.same_home === opt.value}
                                                                onChange={() => answerSameDay(g.ids as string[], opt.value)} />
                                                            {opt.label}
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                            {g.same_home === 'yes' && <p className="text-xs text-ink2 mt-2" data-testid="former-disposal-same-day-combine">{FORMER_DISPOSAL_SAME_DAY_COMBINE}</p>}
                                            {(g.same_home === 'unsure' || g.same_home === null) && <p className="text-[11px] text-ink3 mt-2">{FORMER_DISPOSAL_SAME_DAY_UNSURE}</p>}
                                        </div>
                                    ))}
                                    {formerSameDay.undated.length > 0 && (
                                        <p className="text-xs text-ink2 p-2 rounded border border-[#E8D89A] bg-[#FAF3E0]" data-testid="former-disposal-undated">{FORMER_DISPOSAL_UNDATED}</p>
                                    )}
                                </div>
                            )}
                            <button type="button" className="text-xs font-bold text-[#7A5A00] underline" onClick={addFormerDisposal} data-testid="former-disposal-add">
                                {formerDisposals ? 'Add another home or disposal day' : FORMER_DISPOSALS_ADD}
                            </button>
                            {formerNomination.show && (
                                <div className="p-3 rounded-lg border border-[#E8D89A] bg-[#FAF3E0]" data-testid="former-disposal-nomination">
                                    <p className="text-sm font-bold text-ink2 mb-2">{FORMER_DISPOSAL_NOMINATION_QUESTION}</p>
                                    <div className="space-y-1" role="radiogroup">
                                        {formerNomination.choices.map((d) => (
                                            <label key={String(d.id)} className="flex items-center gap-2 cursor-pointer text-sm">
                                                <input type="radio" name="former_disposal_nomination" value={String(d.id)}
                                                    checked={downsizing.nominated_disposal_id === d.id}
                                                    onChange={() => writeDownsizing({ ...downsizing, nominated_disposal_id: d.id })} />
                                                {formerDisposalLabel(d, (formerDisposals ?? []).indexOf(d))}
                                            </label>
                                        ))}
                                    </div>
                                    <p className="text-[10px] text-ink3 mt-2">{FORMER_DISPOSAL_NOMINATION_HELP}</p>
                                </div>
                            )}
                            <div>
                                <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">
                                    Value of everything else passing to direct descendants (£)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    className="w-full text-sm border rounded-lg p-2 focus:ring-2 focus:ring-[#7A5A00]/20 focus:outline-none"
                                    placeholder="e.g. 200000"
                                    value={data.iht_details?.downsizing_allowance?.qualifying_assets_value ?? ''}
                                    onChange={e => update(['iht_details', 'downsizing_allowance', 'qualifying_assets_value'],
                                        e.target.value ? Number(e.target.value) : null)}
                                />
                                {/* D33: the cap is VT attributable to closely inherited property other than the home that counts
                                    (s.8FA(8)) or, with no such home, to any closely inherited property (s.8FB(7)); land counts. */}
                                <p className="text-[10px] text-ink3 mt-1">
                                    {DOWNSIZING_CAP_HELP}
                                </p>
                            </div>
                            {/* s.8L claim — the downsizing addition is only available if claimed (s.8FA(7)). Never defaulted. */}
                            <div>
                                <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">
                                    Is a claim for the downsizing addition being made?
                                </label>
                                <div className="flex gap-2">
                                    {(['yes', 'no', 'unsure'] as const).map(val => (
                                        <label key={val} className={`flex items-center gap-1.5 cursor-pointer p-1.5 rounded border text-xs transition-all ${data.iht_details?.downsizing_allowance?.claim === val
                                            ? 'bg-[#FAF3E0] border-[#E8D89A] ring-1 ring-[#E8D89A] font-bold text-[#7A5A00]'
                                            : 'bg-white border-rule hover:border-[#E8D89A] text-ink2'
                                            }`}>
                                            <input
                                                type="radio" name="downsizing_claim"
                                                className="text-[#7A5A00] focus:ring-[#7A5A00]"
                                                checked={data.iht_details?.downsizing_allowance?.claim === val}
                                                onChange={() => update(['iht_details', 'downsizing_allowance', 'claim'], val)}
                                            />
                                            {val === 'yes' ? 'Yes' : val === 'no' ? 'No' : 'Not sure'}
                                        </label>
                                    ))}
                                </div>
                                <p className="text-[10px] text-ink3 mt-1">
                                    The downsizing addition only counts if it is claimed. Until you answer Yes, it is left out of the estimate.
                                </p>
                            </div>
                            <div className="text-xs text-[#7A5A00] bg-[#FAF3E0] p-2 rounded border border-[#E8D89A]">
                                The "lost RNRB" from the former property can be claimed against assets passing to direct descendants. The addition is capped at the qualifying assets value and subject to the estate taper (IHTM46065).
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* 6. Trust / Gift With Reservation Screening */}
            <div className="bg-white p-6 rounded-xl border border-rule animate-in fade-in slide-in-from-bottom-4">
                <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-accent-soft rounded-lg text-accent">
                        <Shield className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-ink">Trusts & Gifts With Reservation</h3>
                        <p className="text-xs text-ink3">Quick screening for assets that may need specialist IHT treatment (IHTA84 s102+)</p>
                    </div>
                </div>

                <div className="bg-paper p-4 rounded-lg border border-rule-soft space-y-5">
                    {/* Screening Q1: Created or transferred */}
                    <div>
                        <p className="text-sm font-medium text-ink2 mb-1">
                            Did the deceased create or transfer assets into any trusts?
                        </p>
                        <p className="text-[10px] text-ink3 mb-2">
                            This includes family trusts, discretionary trusts, and bare trusts.
                        </p>
                        <div className="flex gap-3">
                            {([true, false] as const).map(val => (
                                <label key={String(val)} className={`flex items-center gap-2 cursor-pointer p-2.5 rounded border text-sm transition-all ${ts?.created_or_transferred_trust === val
                                    ? val ? 'bg-accent-soft border-accent ring-1 ring-accent font-bold text-accent' : 'bg-white border-rule ring-1 ring-rule font-bold'
                                    : 'bg-white border-rule hover:border-accent text-ink2'
                                    }`}>
                                    <input type="radio" name="ts_q1" className="text-accent focus:ring-accent"
                                        checked={ts?.created_or_transferred_trust === val}
                                        onChange={() => {
                                            const updates: [string[], any][] = [[['iht_details', 'trust_screening', 'created_or_transferred_trust'], val]];
                                            if (!val) {
                                                updates.push([['iht_details', 'trust_screening', 'trust_type'], null]);
                                            }
                                            updateMany(updates);
                                        }}
                                    />
                                    <span className="font-bold text-sm">{val ? 'Yes' : 'No'}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Screening Q2: Benefited from trust */}
                    <div className="pt-3 border-t border-rule">
                        <p className="text-sm font-medium text-ink2 mb-1">
                            Did the deceased benefit from a trust during their lifetime?
                        </p>
                        <p className="text-[10px] text-ink3 mb-2">
                            For example: receiving income from a life interest trust, or being a beneficiary of a discretionary trust.
                        </p>
                        <div className="flex gap-3">
                            {([true, false] as const).map(val => (
                                <label key={String(val)} className={`flex items-center gap-2 cursor-pointer p-2.5 rounded border text-sm transition-all ${ts?.benefited_from_trust === val
                                    ? val ? 'bg-accent-soft border-accent ring-1 ring-accent font-bold text-accent' : 'bg-white border-rule ring-1 ring-rule font-bold'
                                    : 'bg-white border-rule hover:border-accent text-ink2'
                                    }`}>
                                    <input type="radio" name="ts_q2" className="text-accent focus:ring-accent"
                                        checked={ts?.benefited_from_trust === val}
                                        onChange={() => update(['iht_details', 'trust_screening', 'benefited_from_trust'], val)}
                                    />
                                    <span className="font-bold text-sm">{val ? 'Yes' : 'No'}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Screening Q3: Gift with Reservation */}
                    <div className="pt-3 border-t border-rule">
                        <p className="text-sm font-medium text-ink2 mb-1">
                            Did the deceased give away any assets but continue to use or benefit from them?
                        </p>
                        <p className="text-[10px] text-ink3 mb-2">
                            For example: giving away a house but continuing to live in it rent-free, or gifting shares but receiving the dividends.
                        </p>
                        <div className="flex gap-3">
                            {([true, false] as const).map(val => (
                                <label key={String(val)} className={`flex items-center gap-2 cursor-pointer p-2.5 rounded border text-sm transition-all ${ts?.has_gift_with_reservation === val
                                    ? val ? 'bg-[#FAF3E0] border-[#E8D89A] ring-1 ring-[#E8D89A] font-bold text-[#7A5A00]' : 'bg-white border-rule ring-1 ring-rule font-bold'
                                    : 'bg-white border-rule hover:border-accent text-ink2'
                                    }`}>
                                    <input type="radio" name="ts_q3" className="text-accent focus:ring-accent"
                                        checked={ts?.has_gift_with_reservation === val}
                                        onChange={() => {
                                            const updates: [string[], any][] = [[['iht_details', 'trust_screening', 'has_gift_with_reservation'], val]];
                                            if (!val) {
                                                updates.push([['iht_details', 'trust_screening', 'gwr_description'], null]);
                                                updates.push([['iht_details', 'trust_screening', 'gwr_value_estimate'], null]);
                                                updates.push([['iht_details', 'trust_screening', 'gwr_asset_type'], null]);
                                                updates.push([['iht_details', 'trust_screening', 'gwr_continued_use'], null]);
                                                updates.push([['iht_details', 'trust_screening', 'gwr_market_rent_paid'], null]);
                                                updates.push([['iht_details', 'trust_screening', 'gwr_records_available'], null]);
                                            }
                                            updateMany(updates);
                                        }}
                                    />
                                    <span className="font-bold text-sm">{val ? 'Yes' : 'No'}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Screening Q4: Uncertainty */}
                    <div className="pt-3 border-t border-rule">
                        <p className="text-sm font-medium text-ink2 mb-1">
                            Are you unsure whether any trusts or reserved-benefit gifts were involved?
                        </p>
                        <p className="text-[10px] text-ink3 mb-2">
                            If uncertain, we'll flag this for further investigation. It's better to highlight uncertainty than to miss a potential issue.
                        </p>
                        <div className="flex gap-3">
                            {([true, false] as const).map(val => (
                                <label key={String(val)} className={`flex items-center gap-2 cursor-pointer p-2.5 rounded border text-sm transition-all ${ts?.uncertain === val
                                    ? val ? 'bg-[#FAF3E0] border-[#E8D89A] ring-1 ring-[#E8D89A] font-bold text-[#7A5A00]' : 'bg-white border-rule ring-1 ring-rule font-bold'
                                    : 'bg-white border-rule hover:border-accent text-ink2'
                                    }`}>
                                    <input type="radio" name="ts_q4" className="text-accent focus:ring-accent"
                                        checked={ts?.uncertain === val}
                                        onChange={() => update(['iht_details', 'trust_screening', 'uncertain'], val)}
                                    />
                                    <span className="font-bold text-sm">{val ? 'Yes, unsure' : 'No, I\'m confident'}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* ─── Follow-up details (only if any Yes/Unsure) ─── */}
                    {(ts?.created_or_transferred_trust === true || ts?.benefited_from_trust === true || ts?.has_gift_with_reservation === true || ts?.uncertain === true) && (
                        <div className="pt-4 border-t-2 border-accent space-y-5 animate-in fade-in slide-in-from-top-2">
                            <p className="text-xs font-bold text-accent uppercase tracking-wide">Follow-up details</p>

                            {/* Trust details (if Q1 or Q2 = Yes) */}
                            {(ts?.created_or_transferred_trust === true || ts?.benefited_from_trust === true) && (
                                <div className="space-y-3 pl-2 border-l-2 border-accent">
                                    <p className="text-xs font-bold text-ink3">Trust details</p>
                                    <div>
                                        <label className="block text-xs font-bold text-ink3 mb-1.5">Type of trust (if known)</label>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {([
                                                ['bare', 'Bare trust (assets held for a named beneficiary)'],
                                                ['interest_in_possession', 'Interest in possession / life interest trust'],
                                                ['discretionary', 'Discretionary trust'],
                                                ['other', 'Other / multiple trusts'],
                                                ['unknown', 'Not sure'],
                                            ] as const).map(([val, label]) => (
                                                <label key={val} className={`flex items-center gap-2 cursor-pointer p-2 rounded border text-[11px] transition-all ${ts?.trust_type === val
                                                    ? 'bg-accent-soft border-accent font-bold text-accent'
                                                    : 'bg-white border-rule hover:border-accent text-ink2'
                                                    }`}>
                                                    <input type="radio" name="trust_type" className="text-accent focus:ring-accent"
                                                        checked={ts?.trust_type === val}
                                                        onChange={() => update(['iht_details', 'trust_screening', 'trust_type'], val)}
                                                    />
                                                    {label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Loan trust sub-question */}
                                    <div className="pt-2 border-t border-rule">
                                        <p className="text-sm font-medium text-ink2 mb-1">
                                            Were any loan trusts or discounted gift trust schemes used?
                                        </p>
                                        <p className="text-[10px] text-ink3 mb-2">
                                            These are specialist financial planning products that may affect the IHT position.
                                        </p>
                                        <div className="flex gap-3">
                                            {([true, false] as const).map(val => (
                                                <label key={String(val)} className={`flex items-center gap-2 cursor-pointer p-2.5 rounded border text-sm transition-all ${ts?.has_loan_trust_or_discounted_gift === val
                                                    ? val ? 'bg-paper border-rule ring-1 ring-rule font-bold text-ink' : 'bg-white border-rule ring-1 ring-rule font-bold'
                                                    : 'bg-white border-rule hover:border-accent text-ink2'
                                                    }`}>
                                                    <input type="radio" name="has_loan_trust" className="text-accent focus:ring-accent"
                                                        checked={ts?.has_loan_trust_or_discounted_gift === val}
                                                        onChange={() => update(['iht_details', 'trust_screening', 'has_loan_trust_or_discounted_gift'], val)}
                                                    />
                                                    <span className="font-bold text-sm">{val ? 'Yes' : 'No'}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* GWR follow-up details (if Q3 = Yes) */}
                            {ts?.has_gift_with_reservation === true && (
                                <div className="space-y-3 pl-2 border-l-2 border-[#E8D89A]">
                                    <p className="text-xs font-bold text-ink3">Gift with reservation details</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">Type of asset</label>
                                            <div className="flex flex-wrap gap-2">
                                                {([
                                                    ['property', 'Property'],
                                                    ['shares', 'Shares / investments'],
                                                    ['other', 'Other'],
                                                ] as const).map(([val, label]) => (
                                                    <label key={val} className={`flex items-center gap-1.5 cursor-pointer p-2 rounded border text-[11px] transition-all ${ts?.gwr_asset_type === val
                                                        ? 'bg-[#FAF3E0] border-[#E8D89A] font-bold text-[#7A5A00]'
                                                        : 'bg-white border-rule hover:border-[#E8D89A] text-ink2'
                                                        }`}>
                                                        <input type="radio" name="gwr_asset_type" className="text-[#7A5A00] focus:ring-[#7A5A00]"
                                                            checked={ts?.gwr_asset_type === val}
                                                            onChange={() => update(['iht_details', 'trust_screening', 'gwr_asset_type'], val)}
                                                        />
                                                        {label}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">
                                                Estimated value (£)
                                            </label>
                                            <input type="number" min="0"
                                                className="w-full text-sm border rounded-lg p-2 focus:ring-2 focus:ring-[#7A5A00]/20 focus:outline-none"
                                                placeholder="e.g. 250000"
                                                value={ts?.gwr_value_estimate ?? ''}
                                                onChange={e => update(['iht_details', 'trust_screening', 'gwr_value_estimate'],
                                                    e.target.value ? Number(e.target.value) : null)}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">
                                            How did the deceased continue to benefit?
                                        </label>
                                        <input type="text"
                                            className="w-full text-sm border rounded-lg p-2 focus:ring-2 focus:ring-[#7A5A00]/20 focus:outline-none"
                                            placeholder="e.g. Continued living in the property rent-free"
                                            value={ts?.gwr_continued_use ?? ''}
                                            onChange={e => update(['iht_details', 'trust_screening', 'gwr_continued_use'], e.target.value || null)}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] uppercase font-bold text-ink3 mb-1">
                                            Brief description
                                        </label>
                                        <input type="text"
                                            className="w-full text-sm border rounded-lg p-2 focus:ring-2 focus:ring-[#7A5A00]/20 focus:outline-none"
                                            placeholder="e.g. Gave house to daughter in 2019"
                                            value={ts?.gwr_description ?? ''}
                                            onChange={e => update(['iht_details', 'trust_screening', 'gwr_description'], e.target.value || null)}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <p className="text-[10px] uppercase font-bold text-ink3 mb-1">Was market rent or consideration paid?</p>
                                            <div className="flex gap-2">
                                                {([true, false] as const).map(val => (
                                                    <label key={String(val)} className={`flex items-center gap-1.5 cursor-pointer p-2 rounded border text-[11px] transition-all ${ts?.gwr_market_rent_paid === val
                                                        ? val ? 'bg-paper border-rule font-bold text-ink' : 'bg-[#FAF3E0] border-[#E8D89A] font-bold text-[#7A5A00]'
                                                        : 'bg-white border-rule hover:border-[#E8D89A] text-ink2'
                                                        }`}>
                                                        <input type="radio" name="gwr_rent" className="text-[#7A5A00] focus:ring-[#7A5A00]"
                                                            checked={ts?.gwr_market_rent_paid === val}
                                                            onChange={() => update(['iht_details', 'trust_screening', 'gwr_market_rent_paid'], val)}
                                                        />
                                                        {val ? 'Yes' : 'No'}
                                                    </label>
                                                ))}
                                            </div>
                                            {ts?.gwr_market_rent_paid === true && (
                                                <p className="text-[10px] text-ink2 mt-1">If full market consideration was paid, this may not be a GWR.</p>
                                            )}
                                            {ts?.gwr_market_rent_paid === false && (
                                                <p className="text-[10px] text-[#7A5A00] mt-1">No consideration paid — this is likely a gift with reservation (IHTA84 s102).</p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase font-bold text-ink3 mb-1">Are records/documentation available?</p>
                                            <div className="flex gap-2">
                                                {([true, false] as const).map(val => (
                                                    <label key={String(val)} className={`flex items-center gap-1.5 cursor-pointer p-2 rounded border text-[11px] transition-all ${ts?.gwr_records_available === val
                                                        ? 'bg-white border-rule ring-1 ring-rule font-bold'
                                                        : 'bg-white border-rule hover:border-rule text-ink2'
                                                        }`}>
                                                        <input type="radio" name="gwr_records" className="text-ink3 focus:ring-ink2"
                                                            checked={ts?.gwr_records_available === val}
                                                            onChange={() => update(['iht_details', 'trust_screening', 'gwr_records_available'], val)}
                                                        />
                                                        {val ? 'Yes' : 'No'}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Trust confidence indicator */}
                    {(ts?.created_or_transferred_trust != null || ts?.benefited_from_trust != null || ts?.has_gift_with_reservation != null || ts?.uncertain != null) && (
                        <div className={`rounded-lg p-3 text-xs border mt-2 ${trustConfidence === 'specialist_review'
                            ? 'bg-paper border-rule text-ink'
                            : trustConfidence === 'caution'
                                ? 'bg-[#FAF3E0] border-[#E8D89A] text-[#7A5A00]'
                                : 'bg-paper border-rule text-ink'
                            }`}>
                            <div className="flex items-center gap-1.5 font-bold mb-1">
                                {trustConfidence === 'specialist_review' && <><AlertTriangle className="w-3.5 h-3.5" /> Specialist review recommended</>}
                                {trustConfidence === 'caution' && <><AlertTriangle className="w-3.5 h-3.5" /> Additional IHT considerations identified</>}
                                {trustConfidence === 'standard' && <><CheckCircle2 className="w-3.5 h-3.5" /> No trust or GWR complications identified</>}
                            </div>
                            {trustConfidence === 'specialist_review' && (
                                <p>The trust arrangements or financial products identified require specialist IHT advice. This tool cannot calculate the full tax impact of these arrangements. The IHT estimate shown may not include trust-related liabilities.</p>
                            )}
                            {trustConfidence === 'caution' && (
                                <p>Trust or gift-with-reservation assets may form part of the estate for IHT purposes even though they have been given away. The values shown in the IHT estimate may need adjustment. Consider seeking professional advice.</p>
                            )}
                            {trustConfidence === 'standard' && (
                                <p>No trust or gift-with-reservation issues have been identified. The standard IHT calculation should apply.</p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* 7. Spouse Life Interest Trust (IPDI) */}
            <div className="bg-white p-6 rounded-xl border border-rule animate-in fade-in slide-in-from-bottom-4">
                <div className="flex items-center gap-2 mb-4">
                    <div className="p-2 bg-paper rounded-lg text-ink2">
                        <Home className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-ink">Spouse Life Interest Trust</h3>
                        <p className="text-xs text-ink3">Also known as an Immediate Post-Death Interest (IPDI)</p>
                    </div>
                </div>

                <div className="bg-paper p-4 rounded-lg border border-rule-soft space-y-4">
                    <div>
                        <p className="text-sm font-medium text-ink2 mb-1">
                            Did the will leave any assets into a trust where a spouse or civil partner can live in a property for life, or receive income from it?
                        </p>
                        <p className="text-[10px] text-ink3 mb-3">
                            This is common where the deceased wanted to protect assets for children while ensuring the surviving spouse can continue to live in the property or receive investment income.
                        </p>
                        <div className="flex gap-3">
                            {(['yes', 'no', 'unsure'] as const).map(val => (
                                <label key={val} className={`flex items-center gap-2 cursor-pointer p-2.5 rounded border text-sm transition-all ${
                                    data.iht_details?.spouse_life_interest_trust?.exists === val
                                        ? val === 'yes' ? 'bg-paper border-rule ring-1 ring-rule font-bold text-ink'
                                            : 'bg-white border-rule ring-1 ring-rule font-bold'
                                        : 'bg-white border-rule hover:border-sub text-ink2'
                                }`}>
                                    <input type="radio" name="ipdi" className="text-ink2 focus:ring-ink2"
                                        checked={data.iht_details?.spouse_life_interest_trust?.exists === val}
                                        onChange={() => {
                                            const updates: [string[], any][] = [
                                                [['iht_details', 'spouse_life_interest_trust', 'exists'], val],
                                            ];
                                            if (val !== 'yes') {
                                                updates.push([['iht_details', 'spouse_life_interest_trust', 'estimated_value'], null]);
                                            }
                                            updateMany(updates);
                                        }}
                                    />
                                    <span className="font-bold text-sm capitalize">{val}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Value estimate — shown when Yes */}
                    {data.iht_details?.spouse_life_interest_trust?.exists === 'yes' && (
                        <div className="pt-3 border-t border-rule">
                            <label className="text-sm font-medium text-ink2 block mb-1">
                                Estimated value of assets held in the trust
                            </label>
                            <p className="text-[10px] text-ink3 mb-2">
                                For example, the value of the property the spouse has a right to live in.
                            </p>
                            <div className="flex items-center gap-2">
                                <span className="text-ink3 font-medium">£</span>
                                <input
                                    type="number"
                                    min={0}
                                    className="w-40 p-2 border border-rule rounded-lg focus:ring-2 focus:ring-ink2/20 outline-none text-sm"
                                    placeholder="e.g. 300000"
                                    value={data.iht_details?.spouse_life_interest_trust?.estimated_value ?? ''}
                                    onChange={e => {
                                        const v = e.target.value === '' ? null : Number(e.target.value);
                                        update(['iht_details', 'spouse_life_interest_trust', 'estimated_value'], v);
                                    }}
                                />
                            </div>
                            <p className="text-[10px] text-ink2 mt-2 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Spouse exemption will be applied to this value for inheritance tax purposes.
                            </p>
                        </div>
                    )}

                    {/* Unsure warning */}
                    {data.iht_details?.spouse_life_interest_trust?.exists === 'unsure' && (
                        <div className="pt-3 border-t border-rule flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-[#7A5A00] shrink-0 mt-0.5" />
                            <p className="text-xs text-[#7A5A00]">
                                You indicated there may be a trust. This can affect inheritance tax treatment. Consider confirming with a professional whether a life interest trust exists in the will.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
